import {useEffect, useState} from 'react';

import type {Module} from '../Module';

import {AppError} from '../../utils/errors/app';
import {getUuid} from '../../utils/getUuid';
import {ContainerBinder} from '../ContainerBinder';
import {ROOT_CONTAINER_KEY} from '../utils';
import {useInjectionContext} from './useInjectionContext';

const consumerIdsByContainerKey = new Map<string, Set<string>>();

export const useInjectModule = ({modules}: {modules: Module[]}) => {
  const context = useInjectionContext();
  const rootContainer = context.get(ROOT_CONTAINER_KEY);
  const [consumerId] = useState(getUuid());

  useState(() => {
    modules.forEach(module => {
      if (!consumerIdsByContainerKey.has(module.id)) {
        consumerIdsByContainerKey.set(module.id, new Set());
      }

      const importers = consumerIdsByContainerKey.get(module.id)!;

      importers.add(consumerId);
    });
  });

  if (!rootContainer) {
    throw new AppError("Root container isn't defined");
  }

  modules.forEach(module => {
    if (!context.has(module.id) && rootContainer) {
      const containerToBind = ContainerBinder.createContainer(rootContainer);

      const container = module.containerFactory({containerToBind});

      context.set(module.id, container);
    }
  });

  useEffect(() => {
    return () => {
      modules.forEach(module => {
        const importers = consumerIdsByContainerKey.get(module.id)!;

        if (importers) {
          importers.delete(consumerId);

          if (importers.size === 0) {
            context.get(module.id)?.unbindAll();
            context.delete(module.id);
            consumerIdsByContainerKey.delete(module.id);
          }
        }
      });
    };
  }, [consumerId, context, modules, rootContainer]);

  return context.get(module.id);
};
